import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { C, card, label, btn, input } from "../lib/theme";
import PlaceField from "../lib/PlaceField";
import { POLICY_VERSION } from "../lib/policy";

/* First-run wizard, shown while me.onboarded_at is null. Sequences the steps the beta
   signup calls for AFTER the auth provider has verified email + set a password:
   public username -> birth information -> research consent -> done.
   Robust to partial state: a user who already has a username or self chart resumes
   at the right step. The card/billing step belongs to the app phase (Stripe Checkout),
   not here. */

const SIGNS = ["Aries","Taurus","Gemini","Cancer","Leo","Virgo","Libra",
  "Scorpio","Sagittarius","Capricorn","Aquarius","Pisces"];
const signOf = (lon) => SIGNS[Math.floor(((lon % 360) + 360) % 360 / 30)];

export default function Onboarding({ me, onDone }) {
  const [step, setStep] = useState(0);
  const [username, setUsername] = useState(me?.display_name || "");
  // time deliberately starts EMPTY: a silent 12:00 default once shipped a wrong Ascendant.
  const [birth, setBirth] = useState({ date: "", time: "", lat: "", lon: "", place: "" });
  const [timeUnknown, setTimeUnknown] = useState(false);
  const [chart, setChart] = useState(null);
  const [consent, setConsent] = useState(true);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  // Resume: skip steps already satisfied (e.g. seeded/returning accounts).
  useEffect(() => {
    let start = 0;
    if (me?.display_name) start = 1;
    api.charts().then((cs) => {
      const self = cs.find((c) => c.is_self);
      if (self) { setChart(self); start = Math.max(start, 2); }
      setStep(start);
    }).catch(() => setStep(start));
  }, [me]);

  const saveUsername = async () => {
    setBusy(true); setErr("");
    try {
      await api.profile({ display_name: username.trim() });
      setStep(1);
    } catch (e) {
      setErr(e.status === 409 ? "That username is taken — try another." : e.message);
    } finally { setBusy(false); }
  };

  const saveBirth = async () => {
    setBusy(true); setErr("");
    try {
      const created = await api.createChart({
        label: "My chart", interactive: true,
        birth_local: `${birth.date}T${timeUnknown ? "12:00" : birth.time}`,
        lat: Number(birth.lat), lon: Number(birth.lon), place_label: birth.place.trim(),
      });
      setChart(created);
      setStep(2);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const finish = async () => {
    setBusy(true); setErr("");
    try {
      await api.onboard({ chart_id: chart.id, research_consent: consent,
        policy_version: POLICY_VERSION, answers: [] });
      onDone();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const steps = ["Username", "Birth details", "Finish"];

  return (
    <Shell step={step} steps={steps}>
      {step === 0 && (
        <>
          <H>Choose your public username</H>
          <P>This is how you'll appear on the message board and to other members. You can't
            change it often, so pick one you like. Letters, numbers, and spaces are fine.</P>
          <input autoFocus value={username} placeholder="e.g. Nightsect Nia"
            onChange={(e) => setUsername(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && username.trim().length >= 3) saveUsername(); }}
            style={{ ...input, width: "100%", boxSizing: "border-box", marginBottom: 6 }} />
          <div style={{ fontSize: 11, color: C.faint }}>3–30 characters. Must be unique.</div>
          <Nav>
            <span />
            <button style={btn()} disabled={busy || username.trim().length < 3} onClick={saveUsername}>
              {busy ? "Checking…" : "Continue"}
            </button>
          </Nav>
        </>
      )}

      {step === 1 && (
        <>
          <H>Your birth information</H>
          <P>Your chart is computed from the date, time, and place of your birth. An accurate
            birth time matters — the Ascendant and the whole-sign houses depend on it. If you're
            unsure of the exact minute, use your best estimate; you can refine it later.</P>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
            <div>
              <FieldLabel>Birth date</FieldLabel>
              <input type="date" value={birth.date}
                onChange={(e) => setBirth((b) => ({ ...b, date: e.target.value }))} style={input} />
            </div>
            <div>
              <FieldLabel>Birth time</FieldLabel>
              <input type="time" value={birth.time} disabled={timeUnknown}
                onChange={(e) => setBirth((b) => ({ ...b, time: e.target.value }))}
                style={{ ...input, opacity: timeUnknown ? 0.45 : 1 }} />
            </div>
          </div>
          <label style={{ display: "flex", gap: 8, alignItems: "center", cursor: "pointer",
            fontSize: 12, color: C.dim, marginBottom: 8 }}>
            <input type="checkbox" checked={timeUnknown}
              onChange={(e) => { setTimeUnknown(e.target.checked);
                if (e.target.checked) setBirth((b) => ({ ...b, time: "" })); }}
              style={{ accentColor: C.brass }} />
            <span>I don't know my birth time — use 12:00 noon (houses and Ascendant will be
              approximate)</span>
          </label>
          <div style={{ marginBottom: 6 }}>
            <FieldLabel>Birthplace</FieldLabel>
            <PlaceField initialPlace={birth.place}
              onResolve={({ place, lat, lon }) => setBirth((b) => ({ ...b, place, lat, lon }))} />
          </div>
          <div style={{ fontSize: 10.5, color: C.faint, marginTop: 6 }}>
            Pick your birthplace and the timezone — including historical daylight saving — is
            resolved from its coordinates automatically.
          </div>
          <Nav>
            <button style={btn(false)} disabled={busy} onClick={() => setStep(0)}>Back</button>
            <button style={btn()} onClick={saveBirth}
              disabled={busy || !birth.date || (!timeUnknown && !birth.time)
                || birth.lat === "" || birth.lon === ""}>
              {busy ? "Computing chart…" : "Continue"}
            </button>
          </Nav>
        </>
      )}

      {step === 2 && (
        <>
          <H>One last thing</H>
          {chart && (
            <div style={{ ...card, padding: "10px 14px", marginBottom: 12, fontSize: 13 }}>
              <b>{chart.asc_lon != null && `${Math.floor(chart.asc_lon % 30)}° `}{chart.asc_sign} rising</b>
              {chart.positions?.Sun != null && <> · {signOf(chart.positions.Sun)} Sun</>}
              {chart.positions?.Moon != null && <> · {signOf(chart.positions.Moon)} Moon</>}
              <span style={{ color: C.faint }}> · {chart.sect} chart</span>
              <div style={{ fontSize: 11, color: C.faint, marginTop: 3 }}>
                Doesn't look right? Go back and double-check the birth time and place.
              </div>
            </div>
          )}
          <P>Your chart is ready. Before you go in, a note on research: with your permission,
            the events you log may be included — <b>anonymously and only in aggregate</b> — in
            the patterns we study across many charts. Your birth details and any private notes
            are never part of that, and you can change this anytime in your profile.</P>
          <label style={{ display: "flex", gap: 10, alignItems: "flex-start", cursor: "pointer",
            fontSize: 13, color: C.text, margin: "10px 0" }}>
            <input type="checkbox" checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              style={{ accentColor: C.brass, marginTop: 2 }} />
            <span>Yes, include my logged events in anonymous aggregate research.
              <span style={{ color: C.faint }}> (Optional — leaving this unchecked doesn't limit
              your use of the app.)</span></span>
          </label>
          <Nav>
            <button style={btn(false)} disabled={busy} onClick={() => setStep(1)}>Back</button>
            <button style={btn()} disabled={busy || !chart} onClick={finish}>
              {busy ? "Finishing…" : "Enter the calendar"}
            </button>
          </Nav>
        </>
      )}

      {err && <div style={{ color: C.rose, fontSize: 12, marginTop: 12 }}>{err}</div>}
    </Shell>
  );
}

const H = ({ children }) => (
  <div style={{ fontFamily: C.displayFont, fontSize: 23, marginBottom: 6 }}>{children}</div>);
const P = ({ children }) => (
  <p style={{ fontSize: 13, color: C.dim, lineHeight: 1.65, margin: "0 0 12px" }}>{children}</p>);
const FieldLabel = ({ children }) => (
  <div style={{ fontSize: 10, letterSpacing: "0.08em", color: C.faint, marginBottom: 4 }}>{children}</div>);
const Nav = ({ children }) => (
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 18 }}>
    {children}</div>);

function Shell({ step, steps, children }) {
  return (
    <div style={{ minHeight: "100vh", background: C.bg, color: C.text, fontFamily: C.font,
      display: "flex", alignItems: "center", justifyContent: "center", padding: "30px 16px" }}>
      <div style={{ width: 480, maxWidth: "100%" }}>
        <div style={{ fontFamily: C.displayFont, fontSize: 26, marginBottom: 4 }}>Astrology Calendar</div>
        <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
          {steps.map((s, i) => (
            <div key={s} style={{ flex: 1 }}>
              <div style={{ height: 4, borderRadius: 2,
                background: i <= step ? C.brass : C.surface2 }} />
              <div style={{ fontSize: 9.5, letterSpacing: "0.06em", marginTop: 4,
                color: i === step ? C.brass : C.faint }}>{s.toUpperCase()}</div>
            </div>
          ))}
        </div>
        <div style={card}>{children}</div>
      </div>
    </div>
  );
}
